import { Droppable } from 'react-beautiful-dnd';
import { Dropdown, IconThreeDotsVer, Typography, Wrapper } from '@screentone/core';
import classnames from 'classnames';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { RenderIf } from 'components/render-if/RenderIf';
import { useAlert } from 'contexts/alert/useAlert';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DROPPABLE_ID_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { useGroup } from 'contexts/group/useGroup';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { MultiZoneModuleContainer, PageModule, StructureGroupConfig, UiModuleType } from 'data/generated/graphql';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { AddModuleButton } from 'features/section-page-edit/components/add-module-button/AddModuleButton';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import useModuleOrderOfAppearance from 'hooks/groups/useModuleOrderOfAppearance';
import {
  canRemoveModuleFromGroup,
  getEffectiveItemLimits,
  getNumTotalItemsBeforeIndex,
  getTreatmentType,
  isFirstModuleInGroup
} from 'utils/modules';
import styles from './MultiZoneModule.module.scss';
import { ModuleItemCount } from '../components/module-item-count/ModuleItemCount';
import { ModuleRibbonIndicator } from '../components/module-ribbon-indicator/ModuleRibbonIndicator';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { TreatmentTypeDropdown } from '../components/treatment-type-dropdown/TreatmentTypeDropdown';
import { UiModuleFieldsHeader } from '../components/ui-module-fields-header/UiModuleFieldsHeader';

interface ModuleProps extends CommonEntityProps {
  data: MultiZoneModuleContainer;
}

export const MultiZoneModule = ({
  data: module,
  parentHierarchyId,
  index,
  isHistory,
  groupId,
  isReadOnly
}: ModuleProps) => {
  const { generateHierarchyId, renderEntity, getGroupConfig } = useDataModelContext();
  const { renderActions: renderMenuActions, hasRenderActions: hasMenuActions } = useContextMenuActions();
  const hierarchyId = generateHierarchyId(module, parentHierarchyId, index);
  const { group: currentGroup } = useGroup();
  const getModuleOrderOfAppearance = useModuleOrderOfAppearance();

  const { alertState } = useAlert();
  const moduleHasValidationError =
    !isHistory && alertState.pageValidationError?.moduleErrors.some((error) => error.pathToModule === hierarchyId);

  const { getTreatmentTypeSetting, currentLayout, addableUiModules, pageTypeSetting } = usePageTypeSettingsContext();
  const treatmentTypeSetting = getTreatmentTypeSetting(module.attributes.pageModule as PageModule);
  const treatmentType = getTreatmentType(module.attributes.pageModule as PageModule);
  // SETTINGS_ONLY(structure-migration): settingsGroupConfig comes from hardcoded SETTINGS; remove once all pubs are migrated
  const settingsGroupConfig = currentLayout?.groupConfigs?.find((gc) => gc.id === groupId);
  // END SETTINGS_ONLY(structure-migration)
  const structureGroupConfig = getGroupConfig(groupId ?? '') as StructureGroupConfig | undefined;
  // structureGroupConfig (structure in DynamoDB — new data shape) takes priority.
  const groupConfig = structureGroupConfig ?? settingsGroupConfig; // remove ?? settingsGroupConfig when SETTINGS_ONLY is deleted
  const {
    min: effectiveMin,
    max: effectiveMax,
    hasLimitOverride
  } = getEffectiveItemLimits(module.attributes.pageModule as PageModule, treatmentTypeSetting);

  const numTotalItemsUsed = getNumTotalItemsBeforeIndex(module, module.collection.length);

  const droppablePrefix = isHistory ? DROPPABLE_ID_PREFIXES.HISTORY : DROPPABLE_ID_PREFIXES.MODULE_ITEMS;
  const actionsType = isHistory ? 'historyModule' : 'draftModule';

  const isAvailableLayoutModuleType = (currentLayout?.availableLayoutModules ?? [])
    .map((m) => m.defaultModule.uiModuleType)
    .includes((module.attributes.pageModule as PageModule).uiModuleType);

  const isFeedModule = module.attributes.pageModule?.uiModuleType === UiModuleType.UiFeedsModuleType;

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isDropDisabled = isHistory || isFeedModule || isReadOnly;

  // Structure pubs: removableTreatmentTypes lists which TTs an editor can remove from the group;
  // anything not listed is implicitly required. Old-shape pubs use minimumAllowed instead (a count
  // floor on the group), with no per-TT granularity. Both shapes feed into canRemoveModuleFromGroup
  // so the remove-from-group action is hidden correctly for either data shape.
  const canRemoveFromGroup = canRemoveModuleFromGroup(
    groupConfig
      ? {
          removableTreatmentTypes: groupConfig.removableTreatmentTypes ?? null,
          canToggle: groupConfig.canToggle ?? undefined,
          min: structureGroupConfig?.min ?? undefined,
          minimumAllowed: settingsGroupConfig?.minimumAllowed
        }
      : undefined,
    treatmentType,
    currentGroup.collection.length
  );

  const shouldShowMenuActions = hasMenuActions(actionsType, {
    entity: module,
    hierarchyId,
    index,
    extraProperties: {
      numTotalItemsUsed,
      canRemoveFromGroup
    }
  });

  const menuActions = renderMenuActions(actionsType, {
    entity: module,
    hierarchyId,
    index,
    extraProperties: {
      numTotalItemsUsed,
      canRemoveFromGroup
    }
  });

  if (module.attributes.zones && groupConfig) {
    const moduleOrder = getModuleOrderOfAppearance({
      moduleIndex: index!,
      treatmentType
    });

    const moduleIsFirstInGroup = isFirstModuleInGroup(module, currentGroup.collection);
    const isDraggable = treatmentType != null && (groupConfig.draggableTreatmentTypes ?? []).includes(treatmentType);
    const treatmentTypeMessageExists = typeof treatmentTypeSetting?.message === 'string';

    return (
      <Wrapper
        data-testid="multizone-wrapper"
        margin={{ bottom: !isAvailableLayoutModuleType || isHistory ? 'mlg' : 'none' }}
        data-model-hierarchy-id={hierarchyId}
        className={styles.moduleWrapper}
        data-module-order={moduleOrder}
        data-group-id={groupId ?? ''}
        data-is-history={isHistory ? 'true' : 'false'}
      >
        <div
          data-testid="multizone-grid"
          className={classnames({
            [styles.multizoneModuleGridNoCount]: !groupConfig.showModuleCount,
            [styles.multizoneModuleGridWithCount]: groupConfig.showModuleCount
          })}
          data-show-module-count={groupConfig.showModuleCount ? 'true' : 'false'}
        >
          {/* structure-only: groupId is set only when the page has body.structure; old-shape pages never reach this branch */}
          <RenderIf
            condition={typeof index === 'number' && moduleIsFirstInGroup && !!groupId && !isHistory && !isReadOnly}
          >
            <div
              data-testid="multizone-add-above-container"
              className={classnames({
                [styles.fullWidthContainerWithCount]: groupConfig.showModuleCount,
                [styles.fullWidthContainerNoCount]: !groupConfig.showModuleCount
              })}
            >
              <AddModuleButton groupId={groupId!} nextModuleIndex={index!}>
                Add a Module Above
              </AddModuleButton>
            </div>
          </RenderIf>
          {/* end structure-only */}
          {groupConfig.showModuleCount && (
            <Typography
              data-testid="multizone-module-number"
              color="ink"
              variant="note"
              margin={{ bottom: 'none', right: 'md' }}
            >
              {moduleOrder}
            </Typography>
          )}
          <div
            data-testid="multizone-treatment-type-dropdown"
            style={{
              marginBottom: 15
            }}
          >
            <TreatmentTypeDropdown
              module={module}
              pageModule={module.attributes.pageModule as PageModule}
              hierarchyId={hierarchyId}
              showCurrentTreatmentTypeAtTheTop
              // modifyEntity={modifyEntity}
              viewOnly={isHistory}
              pageTypeSetting={pageTypeSetting}
              isReadOnly={isReadOnly}
            />
          </div>
          {shouldShowMenuActions && (
            <div className={classnames([styles.menuActionsWrapper])} data-testid="multizone-menu-actions">
              <Dropdown
                padding={{ all: 'none' }}
                position="right"
                trigger={<IconThreeDotsVer color="asphalt" />}
                data-testid="multizone-ellipsis-dropdown"
              >
                {menuActions}
              </Dropdown>
            </div>
          )}
          <div
            data-testid="multizone-fields-header-container"
            className={classnames({
              [styles.fullWidthContainerWithCount]: groupConfig.showModuleCount,
              [styles.fullWidthContainerNoCount]: !groupConfig.showModuleCount
            })}
          >
            <UiModuleFieldsHeader pageModule={module.attributes.pageModule as PageModule} />
            {module.attributes.ribbonUrl && <ModuleRibbonIndicator ribbonUrl={module.attributes.ribbonUrl} />}
          </div>
          <div
            data-testid="multizone-zones-container"
            className={classnames({
              [styles.fullWidthContainerWithCount]: groupConfig.showModuleCount,
              [styles.fullWidthContainerNoCount]: !groupConfig.showModuleCount
            })}
          >
            {module.attributes.zones.map((zone, zoneIdx) => (
              <div
                key={`${zone.name}-${zoneIdx}`}
                className={classnames([styles.zoneWrapper])}
                data-testid="multizone-zone-wrapper"
                data-zone-index={zoneIdx}
                data-zone-name={zone.name}
              >
                <Typography data-testid="multizone-zone-name" className={styles.zoneName} color="asphalt">
                  {zone.name}
                </Typography>
                <Wrapper
                  padding={{ all: 'xs' }}
                  margin={{ bottom: 'xs', right: isDraggable ? 'xs' : 'lg' }}
                  className={classnames(styles.moduleBox, {
                    [styles.moduleFeedsArea]: isFeedModule,
                    [styles.moduleDashedArea]: !isFeedModule
                  })}
                  data-testid="multizone-zone-box"
                  data-error={moduleHasValidationError ? 'true' : 'false'}
                  data-is-feed-module={isFeedModule ? 'true' : 'false'}
                >
                  <Droppable
                    droppableId={`${droppablePrefix}${hierarchyId}_zone-${zone.name}`}
                    isDropDisabled={isDropDisabled}
                  >
                    {(droppableProvided, snapshot) => (
                      <div
                        data-testid="multizone-zone-droppable"
                        className={classnames(styles.moduleDroppableArea, {
                          [styles.zoneCollapsed]: zone.contentIndexes?.length === 0 && !snapshot.isDraggingOver,
                          [styles.zoneExpanded]:
                            Array.isArray(zone.contentIndexes) &&
                            zone.contentIndexes.length > 0 &&
                            !snapshot.isDraggingOver
                        })}
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                      >
                        {Array.isArray(zone.contentIndexes) &&
                          zone.contentIndexes.map((collectionItemIndex, contentIndex) => {
                            const item = module.collection[collectionItemIndex];

                            const articleHierarchyId = generateHierarchyId(item, hierarchyId, contentIndex);
                            const articleActionsType = isHistory ? 'historyArticle' : 'draftArticle';

                            return (
                              <div
                                key={`${item.attributes.id}-${articleHierarchyId}`}
                                data-testid="multizone-zone-item"
                                data-item-index={contentIndex}
                              >
                                {renderEntity(item, {
                                  hierarchyId,
                                  index: contentIndex,
                                  isHistory,
                                  extraProps: {
                                    content: {
                                      // availableContentSlots,
                                      isDraft: !isHistory
                                    },
                                    article: {
                                      renderActions: () =>
                                        renderMenuActions(articleActionsType, {
                                          hierarchyId: articleHierarchyId,
                                          entity: item,
                                          index: contentIndex
                                        })
                                    },
                                    module: {
                                      treatmentTypeSetting
                                    }
                                  },
                                  isReadOnly
                                })}
                              </div>
                            );
                          })}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </Wrapper>
              </div>
            ))}
          </div>
          <RenderIf condition={!treatmentTypeMessageExists || hasLimitOverride}>
            <ModuleItemCount
              min={effectiveMin}
              max={effectiveMax}
              showMaxInDenominator
              count={numTotalItemsUsed}
              contentTypes={treatmentTypeSetting?.contentTypes}
              inMultiZoneModule
              data-testid="multizone-module-item-count"
            />
          </RenderIf>
          <RenderIf condition={treatmentTypeMessageExists && !hasLimitOverride}>
            <Typography
              size="sm"
              weight="reg"
              color="asphalt"
              className={classnames({
                [styles.fullWidthContainerNoCount]: !groupConfig.showModuleCount,
                [styles.fullWidthContainerWithCount]: groupConfig.showModuleCount
              })}
              data-testid="multizone-treatment-message"
            >
              {treatmentTypeSetting?.message}
            </Typography>
          </RenderIf>

          <RenderIf
            condition={
              typeof index === 'number' && !groupId && isAvailableLayoutModuleType && !isHistory && !isReadOnly
            }
          >
            <div
              data-testid="multizone-add-below-container-root"
              className={classnames({
                [styles.fullWidthContainerNoCount]: !groupConfig.showModuleCount,
                [styles.fullWidthContainerWithCount]: groupConfig.showModuleCount
              })}
            >
              <AddModuleButton
                addableUiModules={addableUiModules}
                nextModuleIndex={index! + 1}
                parentHierarchyId={parentHierarchyId}
              />
            </div>
          </RenderIf>
          {/* structure-only: groupId is set only when the page has body.structure; old-shape pages never reach this branch */}
          <RenderIf condition={typeof index === 'number' && !!groupId && !isHistory && !isReadOnly}>
            <div
              data-testid="multizone-add-below-container-group"
              className={classnames({
                [styles.fullWidthContainerNoCount]: !groupConfig.showModuleCount,
                [styles.fullWidthContainerWithCount]: groupConfig.showModuleCount
              })}
            >
              <AddModuleButton groupId={groupId!} nextModuleIndex={index! + 1}>
                Add a Module Below
              </AddModuleButton>
            </div>
          </RenderIf>
          {/* end structure-only */}
        </div>
      </Wrapper>
    );
  }

  return null;
};
